import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  API_GET_ALL_TAGS,
  API_CREATE_TAG,
  API_UPDATE_TAG,
  API_DELETE_TAG,
  API_ASSIGN_TAG_TO_CUSTOMER,
  API_REMOVE_TAG_FROM_CUSTOMER,
  API_GET_ASSIGNED_TAGS_BY_CUSTOMER,
} from "../api/tags.services";

/* ===============================
   ASYNC THUNKS
=============================== */

// Fetch all tags
export const fetchAllTags = createAsyncThunk(
  "tag/fetchAllTags",
  async (_, { rejectWithValue }) => {
    try {
      return await API_GET_ALL_TAGS();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Create tag
export const createTag = createAsyncThunk(
  "tag/createTag",
  async (formData, { rejectWithValue }) => {
    try {
      return await API_CREATE_TAG(formData);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Update tag
export const updateTag = createAsyncThunk(
  "tag/updateTag",
  async ({ tagId, formData }, { rejectWithValue }) => {
    try {
      return await API_UPDATE_TAG(tagId, formData);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Delete tag
export const deleteTag = createAsyncThunk(
  "tag/deleteTag",
  async (tagId, { rejectWithValue }) => {
    try {
      await API_DELETE_TAG(tagId);
      return tagId;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Assign tag to customer
export const assignTagToCustomer = createAsyncThunk(
  "tag/assignTagToCustomer",
  async (payload, { rejectWithValue }) => {
    try {
      return await API_ASSIGN_TAG_TO_CUSTOMER(payload);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Remove tag from customer
export const removeTagFromCustomer = createAsyncThunk(
  "tag/removeTagFromCustomer",
  async (payload, { rejectWithValue }) => {
    try {
      return await API_REMOVE_TAG_FROM_CUSTOMER(payload);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Fetch assigned tags by customer (Firebase)
export const fetchAssignedTagsByCustomer = createAsyncThunk(
  "tag/fetchAssignedTagsByCustomer",
  async (customerMobile, { rejectWithValue }) => {
    try {
      return await API_GET_ASSIGNED_TAGS_BY_CUSTOMER(customerMobile);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/* ===============================
   SLICE
=============================== */

const tagSlice = createSlice({
  name: "tag",
  initialState: {
    tags: [],
    assignedTagIds: [],
    loading: false,
    error: null,
    success: false,
  },
  reducers: {
    clearTagError(state) {
      state.error = null;
    },
    clearTagSuccess(state) {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL TAGS
      .addCase(fetchAllTags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTags.fulfilled, (state, action) => {
        state.loading = false;
        state.tags = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchAllTags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // CREATE TAG
      .addCase(createTag.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTag.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.tags.push(action.payload);
      })
      .addCase(createTag.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // UPDATE TAG
      .addCase(updateTag.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTag.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.tags = state.tags.map((tag) =>
          tag.id === action.payload.id ? action.payload : tag
        );
      })
      .addCase(updateTag.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // DELETE TAG
      .addCase(deleteTag.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTag.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.tags = state.tags.filter((tag) => tag.id !== action.payload);
      })
      .addCase(deleteTag.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ASSIGN TAG
      .addCase(assignTagToCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignTagToCustomer.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(assignTagToCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // REMOVE TAG
      .addCase(removeTagFromCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeTagFromCustomer.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(removeTagFromCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // FETCH ASSIGNED TAGS
      .addCase(fetchAssignedTagsByCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAssignedTagsByCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.assignedTagIds = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchAssignedTagsByCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearTagError, clearTagSuccess } = tagSlice.actions;
export default tagSlice.reducer;
